// Copy for /platform/sensor-fusion. This is a native page: its words live here,
// not in Contentstack, so a copy change is a code change and ships with a PR.

export interface ISensorFusionContent {
  seo: { title: string; description: string };
  hero: {
    eyebrow: string;
    headline: string;
    headlineHighlight: string;
    intro: string[];
  };
  pipeline: {
    // Read by screen readers in place of the animated diagram.
    diagramLabel: string;
    hint: string;
    stages: { step: string; name: string; where: string; text: string }[];
  };
  pose: {
    headline: string;
    intro: string;
    fields: { name: string; text: string }[];
  };
  graph: {
    headline: string;
    paragraphs: string[];
    sites: { name: string; text: string }[];
  };
  related: { lead: string; label: string; href: string };
  cta: {
    eyebrow: string;
    headline: string;
    primary: { title: string; href: string };
    secondary: { title: string; href: string };
  };
}

export const SENSOR_FUSION_PATH = '/platform/sensor-fusion';

export const sensorFusionContent: ISensorFusionContent = {
  seo: {
    title: 'Sensor Fusion',
    description:
      'Mapped Sensor Fusion turns every positioning signal on site into one live pose for each person, vehicle and robot, with its frame, time, uncertainty and provenance.',
  },

  hero: {
    eyebrow: 'Sensor Fusion',
    headline: 'Many imperfect signals in.',
    headlineHighlight: 'One governed pose out.',
    intro: [
      'AI can write software, draft contracts and beat grandmasters, but ask it where the forklift is and it has no idea. A site has plenty of positioning data. What it lacks is one answer.',
      'Sensor Fusion gives Physical AI that answer: a single live position for every person, vehicle, robot and asset on site, with the uncertainty stated.',
    ],
  },

  pipeline: {
    diagramLabel:
      'Animated diagram of the Sensor Fusion pipeline. Positioning sources report to a Mapped gateway at the edge, the Mapped cloud fuses them into one pose per subject, and the pose stream feeds the applications that act on it.',
    hint: 'Swipe the diagram, or pick a stage',
    stages: [
      {
        step: '1',
        name: 'Sense',
        where: 'at the edge',
        text: 'Every positioning source on site reports to a Mapped gateway: UWB, Bluetooth, Wi-Fi, cameras, GNSS, robot odometry, and door and gate events. The gateway puts them on one clock and stamps where each measurement came from.',
      },
      {
        step: '2',
        name: 'Fuse',
        where: 'in the Mapped cloud',
        text: 'Measurements are converted to common units, carried into a shared coordinate frame and aligned in time. One solve then produces a pose for each subject. Identical inputs give identical answers, and a conflict between sources is reported rather than averaged away.',
      },
      {
        step: '3',
        name: 'Act',
        where: 'wherever the work is',
        text: 'The pose stream feeds dashboards, fleet software and AI agents. Every consumer gets the same answer, in the coordinate frame it asks for.',
      },
    ],
  },

  pose: {
    headline: 'A position you can check',
    intro:
      'A dot on a map is easy to draw and hard to trust. Every pose from Sensor Fusion carries the four things a system needs before it acts on one.',
    fields: [
      {
        name: 'Frame',
        text: 'A named coordinate frame from a governed hierarchy, so the same pose reads correctly in site, building, floor or global coordinates.',
      },
      {
        name: 'Time',
        text: 'The instant the pose describes, which is not the instant it arrived.',
      },
      {
        name: 'Uncertainty',
        text: 'A stated error, in meters, that widens when a sensor drops out and tightens when it returns.',
      },
      {
        name: 'Provenance',
        text: 'The sensors that contributed, so a consumer can decide how much weight to give it.',
      },
    ],
  },

  graph: {
    headline: 'In the same model as everything else',
    paragraphs: [
      'Poses land in the knowledge graph Mapped already keeps for a site. A forklift’s position sits beside the rack it is approaching, the door it will pass and the air handler overhead, and one query reaches all of it.',
      'That is what lets an AI agent go from a question to a working application in hours. It asks for things by type, follows relationships that are already there, and now knows where everything is.',
    ],
    sites: [
      {
        name: 'Warehouses',
        text: 'Forklifts, tuggers, autonomous robots, drones and people, tracked together across the floor and the yard.',
      },
      {
        name: 'Datacenters',
        text: 'A rack inlet temperature and the technician walking toward it, in one model at the same instant.',
      },
      {
        name: 'Airports',
        text: 'Gates, jet bridges, baggage and the crews working them, across every level of a terminal.',
      },
    ],
  },

  related: {
    lead: 'Why it matters',
    label: 'Read the Physical AI manifesto',
    href: '/manifesto',
  },

  cta: {
    eyebrow: 'Sensor Fusion',
    headline: 'See where everything is.',
    primary: { title: 'Talk to us', href: '/contact' },
    secondary: { title: 'See the platform', href: '/platform' },
  },
};
